// Customer airtime top-up (Voye send-product, merged into the LakayCart portal).
//
// ZERO-DOWNTIME / ADDITIVE: this page is only reachable when the airtime feature
// flag is ON (see `airtimeEnabled()` in ../../lib/featureFlags). Its route and
// nav item are both gated by the same flag, so in production — flag OFF — nothing
// here is visible or routable. It touches no existing commerce flow; it only
// calls the merged backend's /api/v1/airtime/* endpoints (already server-live,
// airtime_enabled=true) via the shared api client.
//
// Flow, single page: recipient phone → detect operator → pick a product →
// review → confirm (Idempotency-Key). Mirrors the mobile Voye flow, rendered in
// the LakayCart portal design system.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Smartphone, Phone, CheckCircle2, Loader2, ArrowRight, ArrowLeft,
  LayoutDashboard, Package, Gift, Users, CreditCard, History, Repeat,
  Settings, Signal, AlertCircle,
} from 'lucide-react';
import { PortalShell, type NavSection } from '../../components/shared/PortalShell';
import { PaymentPanel } from '../../components/shared/PaymentPanel';
import { Card, SectionHeader, EmptyState, Button } from '../../components/shared/primitives';
import {
  airtime, createAirtimeOrder, ApiError,
  type AirtimeOperatorWire, type AirtimeProductWire, type AirtimeCatalogWire, type AirtimeOrder,
} from '../../lib/api';

// Nav mirrors CustomerDashboard's, with Airtime inserted. Kept local so this
// page is self-contained; when airtime graduates from flagged to default, fold
// this item into the shared CUSTOMER_NAV.
const CUSTOMER_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',     icon: LayoutDashboard, href: '/customer' },
    { id: 'requests',   label: 'Requests',      icon: Package,         href: '/customer/requests' },
    { id: 'airtime',    label: 'Airtime',       icon: Smartphone,      href: '/customer/airtime' },
    { id: 'packages',   label: 'Care packages', icon: Gift,            href: '/customer/packages' },
    { id: 'recipients', label: 'Recipients',    icon: Users,           href: '/customer/recipients' },
  ]},
  { title: 'Payments', items: [
    { id: 'methods',    label: 'Payment methods', icon: CreditCard, href: '/customer/payment-methods' },
    { id: 'history',    label: 'History',         icon: History,    href: '/customer/history' },
    { id: 'recurring',  label: 'Recurring',       icon: Repeat,     href: '/customer/recurring' },
  ]},
  { title: 'Account', items: [
    { id: 'settings',   label: 'Settings',        icon: Settings,   href: '/customer/settings' },
  ]},
];

// `pay` sits between review and done: the order exists but is NOT
// fulfilled until the card is charged.
type Step = 'phone' | 'product' | 'review' | 'pay' | 'done';

function newIdempotencyKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ik_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

// Reloadly/DT One return amounts in the product's own currency (e.g. HTG or USD)
// as a whole number of major units — display verbatim with the wire currency,
// never re-convert (that is the provider's number, not ours).
function formatProductAmount(p: AirtimeProductWire): string {
  return `${p.currency} ${p.amount.toLocaleString()}`;
}

/** Integer minor units -> "$15" / "$4.50". */
function fmtUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function AirtimePage() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [operator, setOperator] = useState<AirtimeOperatorWire | null>(null);
  const [catalog, setCatalog] = useState<AirtimeCatalogWire | null>(null);
  const [product, setProduct] = useState<AirtimeProductWire | null>(null);
  // Chosen amount in minor units, for RANGE operators (the launch product).
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [amountInput, setAmountInput] = useState('');
  const [order, setOrder] = useState<AirtimeOrder | null>(null);

  const detectM = useMutation({
    mutationFn: async (p: string) => {
      const det = await airtime.detectOperator(p);
      if (!det.detected || !det.operator) throw new Error('no-operator');
      const cat = await airtime.catalog(det.operator.operator_id);
      return { operator: det.operator, catalog: cat };
    },
    onSuccess: (r) => {
      setOperator(r.operator);
      setCatalog(r.catalog);
      // Default a range to the carrier's most popular amount -- one click for
      // the common case, still editable.
      if (r.catalog.denomination === 'range' && r.catalog.range?.popular) {
        setAmountCents(r.catalog.range.popular);
        setAmountInput(String(r.catalog.range.popular / 100));
      }
      setStep('product');
    },
  });

  const orderM = useMutation({
    mutationFn: async () => {
      if (!operator) throw new Error('incomplete');
      const isRange = catalog?.denomination === 'range';
      if (!isRange && !product) throw new Error('incomplete');
      const res = await createAirtimeOrder(
        {
          operator_id: operator.operator_id,
          // A range operator has no catalog product; the backend keys the
          // top-up off "<operator>-airtime" plus the chosen amount.
          product_ref: isRange ? `${operator.operator_id}-airtime` : product!.product_ref,
          recipient_phone: phone,
          ...(isRange && amountCents !== null ? { amount: amountCents } : {}),
        },
        newIdempotencyKey(),
      );
      return res.order;
    },
    // The order is created PENDING and is NOT fulfilled. The carrier is only
    // called once Stripe confirms the charge, so we move to payment here
    // rather than declaring success.
    onSuccess: (o) => { setOrder(o); setStep('pay'); },
  });

  // Client-side bounds check so the sender gets instant feedback; the server
  // re-validates and is the authority.
  const amountError =
    catalog?.denomination === 'range' && catalog.range && amountCents !== null
      ? amountCents < catalog.range.min
        ? `Minimum is ${fmtUsd(catalog.range.min)}.`
        : amountCents > catalog.range.max
          ? `Maximum is ${fmtUsd(catalog.range.max)}.`
          : null
      : null;

  function reset() {
    setStep('phone'); setPhone(''); setOperator(null);
    setCatalog(null); setProduct(null); setOrder(null);
    setAmountCents(null); setAmountInput('');
    detectM.reset(); orderM.reset();
  }

  return (
    <PortalShell
      nav={CUSTOMER_NAV}
      activeId="airtime"
      title="Send airtime"
      subtitle="Top up a phone in Haiti in seconds."
    >
      <div className="max-w-xl space-y-5">
        <Stepper step={step} />

        {step === 'phone' && (
          <Card>
            <SectionHeader title="Who are you topping up?" subtitle="Enter the recipient's Haitian mobile number." />
            <form
              className="space-y-4"
              onSubmit={(e) => { e.preventDefault(); if (phone.trim()) detectM.mutate(phone.trim()); }}
            >
              <label className="block">
                <span className="text-[12px] font-semibold text-slate-700">Recipient phone</span>
                <div className="mt-1 flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 focus-within:border-brand-500">
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+509 3 800 1234"
                    className="flex-1 text-[14px] outline-none bg-transparent"
                  />
                </div>
              </label>

              {detectM.isError && (
                <p className="text-[12.5px] text-rose-600 inline-flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  We couldn't detect a carrier for that number. Check the number and try again.
                </p>
              )}

              <Button type="submit" fullWidth disabled={!phone.trim() || detectM.isPending}>
                {detectM.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Detecting carrier…</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </form>
          </Card>
        )}

        {step === 'product' && operator && (
          <Card>
            <SectionHeader
              title="Choose an amount"
              actions={<button onClick={() => setStep('phone')} className="text-[12px] font-semibold text-slate-500 hover:underline inline-flex items-center gap-0.5"><ArrowLeft className="w-3 h-3" /> Change</button>}
            />
            <p className="-mt-2 mb-3 text-[12px] text-slate-500 inline-flex items-center gap-1.5">
              <Signal className="w-3.5 h-3.5 text-brand-600" /> {operator.operator} · {phone}
            </p>
            {catalog?.denomination === 'range' && catalog.range ? (
              /* RANGE: the sender types any amount within the carrier's bounds.
                 There is no package list for Haitian airtime. */
              <div>
                <label htmlFor="airtime-amount" className="block text-[12.5px] font-semibold text-slate-700">
                  Amount ({catalog.currency})
                </label>
                <div className={`mt-1 flex items-center rounded-lg border bg-white px-3 ${amountError ? 'border-rose-300' : 'border-slate-200'}`}>
                  <span aria-hidden="true" className="pr-1 text-[17px] font-semibold text-slate-900">$</span>
                  <input
                    id="airtime-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0"
                    value={amountInput}
                    aria-invalid={amountError ? true : undefined}
                    aria-describedby={amountError ? 'airtime-amount-error' : undefined}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Refuse input we would have to guess at, rather than
                      // silently rounding someone's money.
                      if (!/^\d*(\.\d{0,2})?$/.test(raw.trim()) && raw.trim() !== '') return;
                      setAmountInput(raw);
                      const t = raw.trim();
                      setAmountCents(t === '' ? null : Math.round(Number(t) * 100));
                    }}
                    className="w-full bg-transparent py-2.5 text-[17px] tabular-nums outline-none"
                  />
                </div>

                {amountError ? (
                  <p id="airtime-amount-error" role="alert" className="mt-1.5 text-[12px] font-semibold text-rose-600">
                    {amountError}
                  </p>
                ) : (
                  <p className="mt-1.5 text-[11.5px] text-slate-500">
                    {fmtUsd(catalog.range.min)} – {fmtUsd(catalog.range.max)}
                  </p>
                )}

                {catalog.range.suggested.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {catalog.range.suggested.slice(0, 6).map((cents) => (
                      <button
                        key={cents}
                        type="button"
                        aria-pressed={amountCents === cents}
                        onClick={() => { setAmountCents(cents); setAmountInput(String(cents / 100)); }}
                        className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold tabular-nums transition ${
                          amountCents === cents
                            ? 'bg-slate-900 text-white'
                            : 'border border-slate-200 bg-white text-slate-700 hover:border-brand-400'
                        }`}
                      >
                        {fmtUsd(cents)}
                        {cents === catalog.range!.popular && <span className="ml-1 text-[10px] opacity-70">popular</span>}
                      </button>
                    ))}
                  </div>
                )}

                <Button
                  fullWidth
                  disabled={amountCents === null || amountError !== null}
                  onClick={() => setStep('review')}
                  className="mt-4"
                >
                  Review top-up <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            ) : (catalog?.products.length ?? 0) === 0 ? (
              <EmptyState icon={Smartphone} title="No packages available" body="This carrier has no data packages right now." />
            ) : (
              /* FIXED: real bundle packages, with the provider's own copy. */
              <div className="grid grid-cols-2 gap-2.5">
                {(catalog?.products ?? []).map((p) => (
                  <button
                    key={p.product_ref}
                    onClick={() => { setProduct(p); setStep('review'); }}
                    className="text-left rounded-lg border border-slate-200 px-3 py-3 hover:border-brand-400 hover:bg-brand-50/40 transition"
                  >
                    <p className="text-[14px] font-semibold text-slate-900">{formatProductAmount(p)}</p>
                    {p.description && <p className="text-[11.5px] text-slate-500 line-clamp-2">{p.description}</p>}
                    <p className="mt-1 text-[10.5px] uppercase tracking-wider font-semibold text-brand-600">{p.kind}</p>
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}

        {step === 'review' && operator && (product || amountCents !== null) && (
          <Card>
            <SectionHeader title="Confirm your top-up" actions={<button onClick={() => setStep('product')} className="text-[12px] font-semibold text-slate-500 hover:underline inline-flex items-center gap-0.5"><ArrowLeft className="w-3 h-3" /> Back</button>} />
            <dl className="divide-y divide-slate-100 text-[13px]">
              <Row label="Recipient" value={phone} />
              <Row label="Carrier" value={operator.operator} />
              {product ? (
                <>
                  <Row label="Product" value={product.name} />
                  <Row label="Amount" value={formatProductAmount(product)} strong />
                </>
              ) : (
                /* A range top-up has no package -- the chosen amount IS the
                   product. The total is confirmed server-side on submit. */
                <Row label="Top-up amount" value={fmtUsd(amountCents!)} strong />
              )}
            </dl>

            {orderM.isError && (
              <p className="mt-3 text-[12.5px] text-rose-600 inline-flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                {orderM.error instanceof ApiError && orderM.error.status === 402
                  ? 'Payment could not be completed. Check your payment method.'
                  : 'Something went wrong sending this top-up. You have not been charged twice — try again.'}
              </p>
            )}

            <Button onClick={() => orderM.mutate()} disabled={orderM.isPending} fullWidth className="mt-4">
              {orderM.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : (
                <>
                  {/* A RANGE top-up has no product — `product` is null here and
                      formatProductAmount() dereferenced it, crashing the entire
                      review step into the error boundary ("Cannot read
                      properties of null (reading 'currency')"). The chosen
                      amount is the correct label for a range order anyway. */}
                  Send {product ? formatProductAmount(product) : fmtUsd(amountCents ?? 0)}{' '}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </Card>
        )}

        {step === 'pay' && order && (
          <Card>
            <SectionHeader
              title="Payment"
              actions={<button onClick={() => setStep('review')} className="text-[12px] font-semibold text-slate-500 hover:underline inline-flex items-center gap-0.5"><ArrowLeft className="w-3 h-3" /> Back</button>}
            />
            <p className="-mt-2 mb-3 text-[12px] text-slate-500">
              {operator?.operator} · {phone}
            </p>
            {/* The order exists but is NOT fulfilled. The carrier is called
                only after the server confirms this payment. */}
            <PaymentPanel
              payableType="airtime"
              payableId={order.id}
              onPaid={() => setStep('done')}
              onCancel={() => setStep('review')}
            />
          </Card>
        )}

        {step === 'done' && order && (
          <Card>
            <div className="text-center py-4">
              <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="w-6 h-6" />
              </span>
              <h2 className="mt-3 text-[16px] font-extrabold text-[var(--color-ink)]">Airtime on its way</h2>
              <p className="mt-1 text-[13px] text-[var(--color-body)]">{order.currency} {order.amount.toLocaleString()} to {order.recipient_phone}</p>
              <p className="mt-0.5 text-[11.5px] text-[var(--color-muted)] font-mono">Ref {order.provider_reference ?? `ORD-${order.id}`} · {order.status}</p>
              <Button onClick={reset} variant="ghost" className="mt-5">
                Send another
              </Button>
            </div>
          </Card>
        )}
      </div>
    </PortalShell>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? 'font-semibold text-slate-900' : 'text-slate-800'}>{value}</dd>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const order: Step[] = ['phone', 'product', 'review', 'pay', 'done'];
  const labels: Record<Step, string> = {
    phone: 'Phone', product: 'Amount', review: 'Review', pay: 'Pay', done: 'Done',
  };
  const idx = order.indexOf(step);
  return (
    <ol className="flex items-center gap-1.5 text-[11px] font-semibold">
      {order.map((s, i) => (
        <li key={s} className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 ${i <= idx ? 'text-brand-700' : 'text-slate-400'}`}>
            <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] ${i < idx ? 'bg-brand-600 text-white' : i === idx ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-300' : 'bg-slate-100 text-slate-400'}`}>
              {i < idx ? '✓' : i + 1}
            </span>
            {labels[s]}
          </span>
          {i < order.length - 1 && <span className="text-slate-300">·</span>}
        </li>
      ))}
    </ol>
  );
}
